/**
 * Small in-memory cache with stale-while-revalidate, used for server side Sanity queries.
 *
 * Without it, every server render (and every crawler hit) sends its own queries to the
 * Sanity API CDN. A warm server instance now reuses results for `ttlMs`, serves stale data
 * for up to `staleMs` while one background refresh runs, and keeps stale data if a refresh fails.
 */

interface Entry {
  value?: unknown;
  hasValue: boolean;
  fetchedAt: number;
  inflight?: Promise<unknown>;
}

const MAX_ENTRIES = 500;
const store = new Map<string, Entry>();

export const TTL = {
  short: 5 * 60 * 1000,
  long: 10 * 60 * 1000,
  sitemap: 60 * 60 * 1000
};

const copy = <T>(value: T): T =>
  value !== null && typeof value === 'object' ? structuredClone(value) : value;

export async function cached<T>(
  key: string,
  ttlMs: number,
  loader: () => Promise<T>,
  staleMs: number = ttlMs * 6
): Promise<T> {
  const now = Date.now();
  let entry = store.get(key);

  if (!entry) {
    if (store.size >= MAX_ENTRIES) {
      // drop the oldest entry
      const oldest = store.keys().next().value;
      if (oldest !== undefined) store.delete(oldest);
    }
    entry = { hasValue: false, fetchedAt: 0 };
    store.set(key, entry);
  }

  const age = now - entry.fetchedAt;

  if (entry.hasValue && age < ttlMs) {
    return copy(entry.value as T);
  }

  const refresh = () => {
    const e = entry as Entry;
    if (!e.inflight) {
      e.inflight = loader()
        .then((value) => {
          e.value = value;
          e.hasValue = true;
          e.fetchedAt = Date.now();
          return value;
        })
        .finally(() => {
          e.inflight = undefined;
        });
    }
    return e.inflight as Promise<T>;
  };

  if (entry.hasValue && age < staleMs) {
    // serve stale data right away, refresh in the background
    refresh().catch((error) => console.error('[ttlCache] refresh failed', key, error));
    return copy(entry.value as T);
  }

  try {
    return copy(await refresh());
  } catch (error) {
    if (entry.hasValue) {
      return copy(entry.value as T);
    }
    throw error;
  }
}
